import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    Box,
    Card,
    CardContent,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useMutationAsUser, useQueryAsUser } from "../../clients/graphql/graphql.hooks";
import { KYC_GET_QUERY } from "../../clients/graphql/components/buttons-queries";
import { KYC_UPSERT } from "../../clients/graphql/components/status-queries";
import {ANNUAL_INCOME, INVESTMENT_OBJECTIVE, LIQUID_NET_WORTH, RISK_LEVEL} from '../types/kycStatusEnums';

interface KYCdataButtonProps {
    userKey: number;
}

interface KYCFields {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    birthday?: string;
    liquidNetWorth?: LIQUID_NET_WORTH;
    annualIncome?: ANNUAL_INCOME;
    generalInvestmentObjective?: INVESTMENT_OBJECTIVE;
    riskLevel?: RISK_LEVEL;
}

export function KYCdataButton({ userKey }: KYCdataButtonProps): JSX.Element {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [localForm, setLocalForm] = useState<KYCFields>({});

    const { data: localData, loading: localLoading } = useQueryAsUser(
      KYC_GET_QUERY,
      { userKey, variables: { remote: false } }
    );
    const { data: remoteData, loading: remoteLoading } = useQueryAsUser(
      KYC_GET_QUERY,
      { userKey, variables: { remote: true } }
    );

    const [kycUpsertMutation, kycUpsertMutationStatus] = useMutationAsUser(
      KYC_UPSERT,
      {
          userKey,
          variables: {
              input: {
                  kyc: { ...localForm },
              },
          },
      }
    );

    // Helper function to strip GraphQL-specific fields from KYC data
    const stripGraphQLFields = (kycData: any): KYCFields => {
        const { __typename, ...cleanData } = kycData;
        return cleanData;
    };

    useEffect(() => {
        if (localData?.kycGet?.kyc) {
            setLocalForm(stripGraphQLFields(localData.kycGet.kyc));
        }
    }, [localData]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setEditing(false);
    };

    const handleEdit = () => setEditing(true);

    const handleSave = () => {
        kycUpsertMutation();
        setEditing(false);
    };

    const renderField = (
      label: string,
      name: keyof KYCFields,
      form: KYCFields,
      setForm: React.Dispatch<React.SetStateAction<KYCFields>>,
      displayValue?: string,
      editable: boolean = true
    ) => (
      <Grid item xs={12} sm={6}>
          {editing && editable ? (
            <TextField
              label={label}
              fullWidth
              size="small"
              variant="outlined"
              value={form[name] ?? ""}
              onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
            />
          ) : (
            <Typography variant="body1">
                <strong>{label}:</strong> {displayValue}
            </Typography>
          )}
      </Grid>
    );

    const renderSelectField = <T extends string>(
      label: string,
      name: keyof KYCFields,
      form: KYCFields,
      setForm: React.Dispatch<React.SetStateAction<KYCFields>>,
      options: T[],
      displayValue?: string
    ) => (
      <Grid item xs={12} sm={6}>
          {editing ? (
            <FormControl fullWidth size="small">
                <InputLabel>{label}</InputLabel>
                <Select
                  label={label}
                  value={form[name] as T || ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value as string }))}
                >
                    {options.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                          {opt}
                      </MenuItem>
                    ))}
                </Select>
            </FormControl>
          ) : (
            <Typography variant="body1">
                <strong>{label}:</strong> {displayValue}
            </Typography>
          )}
      </Grid>
    );

    return (
      <>
          <Button variant="outlined" size="small" color="primary" onClick={handleOpen}>
              KYC Data
          </Button>

          <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
              <DialogTitle>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="h6">KYC Data</Typography>
                      {!editing && <Button size="small" onClick={handleEdit}>Edit</Button>}
                  </Box>
              </DialogTitle>

              <DialogContent dividers>
                  <Grid container spacing={2}>
                      {/* Local Data */}
                      <Grid item xs={6}>
                          {localLoading ? (
                            <Typography>Loading...</Typography>
                          ) : localData?.kycGet?.kyc ? (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="h5" gutterBottom>Local Data</Typography>
                                <Card sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            {renderField("First Name", "firstName", localForm, setLocalForm, localData.kycGet.kyc.firstName)}
                                            {renderField("Last Name", "lastName", localForm, setLocalForm, localData.kycGet.kyc.lastName)}
                                            {renderField("Email", "email", localForm, setLocalForm, localData.kycGet.kyc.email)}
                                            {renderField("Address 1", "address1", localForm, setLocalForm, localData.kycGet.kyc.address1)}
                                            {renderField("Address 2", "address2", localForm, setLocalForm, localData.kycGet.kyc.address2)}
                                            {renderField("City", "city", localForm, setLocalForm, localData.kycGet.kyc.city)}
                                            {renderField("State", "state", localForm, setLocalForm, localData.kycGet.kyc.state)}
                                            {renderField("Zip Code", "zipCode", localForm, setLocalForm, localData.kycGet.kyc.zipCode)}
                                            {renderField("Birthday", "birthday", localForm, setLocalForm, localData.kycGet.kyc.birthday)}
                                            {renderSelectField(
                                              "Liquid Net Worth",
                                              "liquidNetWorth",
                                              localForm,
                                              setLocalForm,
                                              Object.values(LIQUID_NET_WORTH),
                                              localData.kycGet.kyc.liquidNetWorth
                                            )}
                                            {renderSelectField(
                                              "Annual Income",
                                              "annualIncome",
                                              localForm,
                                              setLocalForm,
                                              Object.values(ANNUAL_INCOME),
                                              localData.kycGet.kyc.annualIncome
                                            )}
                                            {renderSelectField(
                                              "Investment Objective",
                                              "generalInvestmentObjective",
                                              localForm,
                                              setLocalForm,
                                              Object.values(INVESTMENT_OBJECTIVE),
                                              localData.kycGet.kyc.generalInvestmentObjective
                                            )}
                                            {renderSelectField(
                                              "Risk Level",
                                              "riskLevel",
                                              localForm,
                                              setLocalForm,
                                              Object.values(RISK_LEVEL),
                                              localData.kycGet.kyc.riskLevel
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>
                          ) : (
                            <Typography>No data found</Typography>
                          )}
                      </Grid>

                      {/* Remote Data (read-only) */}
                      <Grid item xs={6}>
                          {remoteLoading ? (
                            <Typography>Loading...</Typography>
                          ) : remoteData?.kycGet?.kyc ? (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="h5" gutterBottom>Institution Data</Typography>
                                <Card sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            {renderField("First Name", "firstName", localForm, setLocalForm, remoteData.kycGet.kyc.firstName, false)}
                                            {renderField("Last Name", "lastName", localForm, setLocalForm, remoteData.kycGet.kyc.lastName, false)}
                                            {renderField("Email", "email", localForm, setLocalForm, remoteData.kycGet.kyc.email, false)}
                                            {renderField("Address 1", "address1", localForm, setLocalForm, remoteData.kycGet.kyc.address1, false)}
                                            {renderField("Address 2", "address2", localForm, setLocalForm, remoteData.kycGet.kyc.address2, false)}
                                            {renderField("City", "city", localForm, setLocalForm, remoteData.kycGet.kyc.city, false)}
                                            {renderField("State", "state", localForm, setLocalForm, remoteData.kycGet.kyc.state, false)}
                                            {renderField("Zip Code", "zipCode", localForm, setLocalForm, remoteData.kycGet.kyc.zipCode, false)}
                                            {renderField("Birthday", "birthday", localForm, setLocalForm, remoteData.kycGet.kyc.birthday, false)}
                                            {renderField("Liquid Net Worth", "liquidNetWorth", localForm, setLocalForm, remoteData.kycGet.kyc.liquidNetWorth, false)}
                                            {renderField("Annual Income", "annualIncome", localForm, setLocalForm, remoteData.kycGet.kyc.annualIncome, false)}
                                            {renderField("Investment Objective", "generalInvestmentObjective", localForm, setLocalForm, remoteData.kycGet.kyc.generalInvestmentObjective, false)}
                                            {renderField("Risk Level", "riskLevel", localForm, setLocalForm, remoteData.kycGet.kyc.riskLevel, false)}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>
                          ) : (
                            <Typography>No data found</Typography>
                          )}
                      </Grid>
                  </Grid>
              </DialogContent>

              <DialogActions>
                  {editing ? (
                    <>
                        <Button onClick={handleSave} color="primary" disabled={kycUpsertMutationStatus.loading}>
                            {kycUpsertMutationStatus.loading ? 'Saving...' : 'Save'}
                        </Button>
                        <Button onClick={handleClose} color="secondary">Cancel</Button>
                    </>
                  ) : (
                    <Button onClick={handleClose} color="primary">Close</Button>
                  )}
              </DialogActions>
          </Dialog>
      </>
    );
}